import React from 'react';
import { Avatar, Box, Grid, Typography } from '@mui/material';
import AvatarEditor from '@components/AvatarEditor';

interface CompanyLogoFieldProps {
  logo: string | null;
  companyName: string;
  avatarEditorOpen: boolean;
  onOpenEditor: () => void;
  onCloseEditor: () => void;
  onSaveLogo: (base64: string | null) => void;
}

/**
 * Seletor de logo da empresa (avatar clicável + AvatarEditor). Extraído de SignupForm.tsx (F7).
 */
export const CompanyLogoField: React.FC<CompanyLogoFieldProps> = ({
  logo,
  companyName,
  avatarEditorOpen,
  onOpenEditor,
  onCloseEditor,
  onSaveLogo,
}) => {
  const initials = (() => {
    if (logo && logo.trim() !== '') return null;
    if (!companyName) return 'E';
    const parts = companyName.trim().split(' ');
    if (parts.length === 1) return companyName.charAt(0).toUpperCase();
    return (parts[0]?.charAt(0) || '') + (parts[parts.length - 1]?.charAt(0) || '');
  })();

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="h6" sx={{ mt: 3, mb: 2, fontWeight: 'bold', color: 'primary.main' }}>
          Dados da Empresa
        </Typography>
      </Grid>
      <Grid item xs={12}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          Logo da Empresa (Opcional)
        </Typography>
        <Box
          sx={{ position: 'relative', cursor: 'pointer' }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onOpenEditor();
          }}
        >
          <Avatar
            {...(logo ? { src: logo } : {})}
            sx={{
              width: 100,
              height: 100,
              border: '3px solid #e0e0e0',
              backgroundColor: logo ? 'transparent' : '#f5f5f5',
              fontSize: '2.5rem',
              fontWeight: 'bold',
              color: logo ? 'inherit' : '#666',
              '& img': {
                objectFit: 'contain',
                width: '100%',
                height: '100%',
              },
              '&:hover': {
                opacity: 0.8,
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            {initials}
          </Avatar>
        </Box>
        <AvatarEditor
          open={avatarEditorOpen}
          onClose={onCloseEditor}
          {...(logo ? { currentAvatar: logo } : {})}
          userName={companyName || 'Empresa'}
          onSave={(base64) => {
            // Normalizar: garantir que sempre tenha o prefixo data:
            let newLogo = base64 || null;
            if (newLogo && !newLogo.startsWith('data:')) {
              if (newLogo.startsWith('image/')) {
                newLogo = `data:${newLogo}`;
              }
            }
            onSaveLogo(newLogo);
            onCloseEditor();
          }}
        />
      </Box>
      </Grid>
    </>
  );
};

export default CompanyLogoField;
